import React, {
	useRef,
	useEffect,
	useImperativeHandle,
	forwardRef,
} from "react";

type CheckboxChangeValue = boolean | null;
type OnChange = (
	el: HTMLInputElement | CheckboxChangeValue,
	value?: CheckboxChangeValue
) => void;

type CheckboxProps = {
	label?: string;
	checked?: boolean | null;
	defaultChecked?: boolean | null;
	controlled?: boolean;
	useIndeterminate?: boolean | ((checked: boolean | null) => boolean);
	onChange?: OnChange;
	align?: string;
	className?: string;
	children?: React.ReactNode;
} & React.InputHTMLAttributes<HTMLInputElement>;

const Checkbox = forwardRef(
	(
		{
			label,
			checked,
			defaultChecked,
			controlled = false,
			useIndeterminate = false,
			onChange,
			align = "items-center",
			className = "",
			children = null,
			...props
		}: CheckboxProps,
		ref: React.Ref<HTMLInputElement>
	) => {
		const inputRef = useRef<HTMLInputElement>(null);

		useImperativeHandle(ref, () => inputRef.current);

		const handleChange = () => {
			if (useIndeterminate === true) {
				if (checked === true) {
					if (controlled) {
						onChange?.(false);
					} else {
						onChange?.(inputRef.current, false);
					}
				} else if (checked === false) {
					if (controlled) {
						onChange?.(null);
					} else {
						onChange?.(inputRef.current, null);
					}
				} else {
					if (controlled) {
						onChange?.(true);
					} else {
						onChange?.(inputRef.current, true);
					}
				}
			} else if (typeof useIndeterminate === "function") {
				const fn = useIndeterminate; // prevent "hook" warning due to "use" prefix
				const result = fn(checked);
				if (controlled) {
					onChange?.(result);
				} else {
					onChange?.(inputRef.current, result);
				}
			} else {
				if (controlled) {
					onChange?.(!checked);
				} else {
					onChange?.(inputRef.current, inputRef.current.checked);
				}
			}
		};

		useEffect(() => {
			if (inputRef.current) {
				if (useIndeterminate && checked === null) {
					inputRef.current.indeterminate = true;
					inputRef.current.checked = false;
				} else if (checked !== null) {
					inputRef.current.indeterminate = false;
					if (!useIndeterminate) {
						inputRef.current.checked = checked;
					}
				}
			}
		}, [useIndeterminate, checked, inputRef]);

		const title =
			(label ? label + ": " : "") +
			(checked === null && useIndeterminate
				? "Indeterminate"
				: checked === true
					? "Checked"
					: "Unchecked");
		return (
			<label
				className={`inline-flex gap-2 ${align} ${className}`}
				title={title}
			>
				<input
					ref={inputRef}
					type="checkbox"
					className="h-5 w-5 text-primary"
					{...(typeof checked !== "undefined"
						? { checked: checked === true }
						: {})}
					{...(typeof defaultChecked !== "undefined"
						? { defaultChecked: defaultChecked === true }
						: {})}
					onChange={handleChange}
					{...props}
				/>
				{(label || children) && (
					<span className="disabled-input-not-allowed">
						{label || children}
					</span>
				)}
			</label>
		);
	}
);
export default Checkbox;
